import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useHelp } from '../context/HelpContext';
import { useSmartPoll } from '../hooks/useSmartPoll';
import NavIcon from './NavIcon';
import Logo from './Logo';

const NAV_GROUPS = [
  {
    key: 'overview',
    label: 'نظرة عامة',
    items: [
      { path: '/', label: 'لوحة التحكم', icon: 'dashboard', roles: ['admin', 'supervisor', 'team'], tour: 'nav-dashboard' },
    ],
  },
  {
    key: 'work',
    label: 'العمل اليومي',
    items: [
      { path: '/tasks', label: 'المهام', icon: 'tasks', roles: ['admin', 'supervisor', 'team'], tour: 'nav-tasks' },
      { path: '/recurring-tasks', label: 'مهام متكررة', icon: 'recurring', roles: ['admin', 'supervisor', 'team'], tour: 'nav-recurring' },
      { path: '/projects', label: 'المشاريع', icon: 'projects', roles: ['admin', 'supervisor', 'team'], tour: 'nav-projects' },
      { path: '/workflows', label: 'المسارات', icon: 'workflows', roles: ['admin', 'supervisor', 'team'], tour: 'nav-workflows' },
      { path: '/book-summaries', label: 'تلخيص الكتب', icon: 'books', roles: ['admin', 'supervisor', 'team'], perm: 'book_summaries.view', tour: 'nav-book-summaries' },
    ],
  },
  {
    key: 'relations',
    label: 'العملاء والداعمون',
    items: [
      { path: '/clients', label: 'العملاء', icon: 'clients', roles: ['admin', 'supervisor', 'team'] },
      { path: '/requests', label: 'طلبات العملاء', icon: 'requests', roles: ['admin', 'supervisor', 'team'] },
      { path: '/contracts', label: 'العقود', icon: 'contracts', roles: ['admin', 'supervisor', 'team'] },
      { path: '/sponsors', label: 'الداعمون', icon: 'sponsors', roles: ['admin', 'supervisor', 'team'] },
    ],
  },
  {
    key: 'reports',
    label: 'التقارير والمالية',
    items: [
      { path: '/reports', label: 'التقارير', icon: 'reports', roles: ['admin', 'supervisor'], tour: 'nav-reports' },
      { path: '/progress-reports', label: 'تقارير التقدّم', icon: 'progress', roles: ['admin', 'supervisor'] },
      { path: '/financial-reports', label: 'التقارير المالية', icon: 'financial', roles: ['admin', 'supervisor'] },
      { path: '/payroll', label: 'مستحقات الفريق', icon: 'financial', roles: ['admin', 'supervisor'], perm: 'payroll.view' },
    ],
  },
  {
    key: 'content',
    label: 'المحتوى والأدوات',
    items: [
      { path: '/transcription', label: 'التفريغ', icon: 'transcription', roles: ['admin', 'supervisor', 'team'], tour: 'nav-transcription' },
      { path: '/transcription/search', label: 'البحث في التفريغات', icon: 'search', roles: ['admin', 'supervisor', 'team'], perm: 'transcription.access' },
      { path: '/tags', label: 'الوسوم', icon: 'tags', roles: ['admin', 'supervisor'] },
    ],
  },
  {
    key: 'admin',
    label: 'الإدارة',
    items: [
      { path: '/users', label: 'المستخدمون', icon: 'users', roles: ['admin', 'supervisor'] },
      { path: '/broadcast', label: 'رسالة عامة', icon: 'broadcast', roles: ['admin', 'supervisor'] },
      { path: '/activity-logs', label: 'سجل العمليات', icon: 'logs', roles: ['admin', 'supervisor', 'team'] },
      { path: '/settings', label: 'الإعدادات', icon: 'settings', roles: ['admin'] },
    ],
  },
  {
    key: 'account',
    label: 'حسابي',
    items: [
      { path: '/notifications', label: 'التنبيهات', icon: 'bell', roles: ['admin', 'supervisor', 'team'] },
      { path: '/approvals', label: 'صندوق الاعتماد', icon: 'bell', roles: ['admin', 'supervisor', 'team'], perm: 'mcp.connect' },
      { path: '/profile', label: 'ملفي', icon: 'profile', roles: ['admin', 'supervisor', 'team'] },
      { path: '/help', label: 'المساعدة', icon: 'help', roles: ['admin', 'supervisor', 'team', 'client'] },
    ],
  },
];

function isNavActive(pathname, path) {
  return path === '/' ? pathname === '/' : pathname.startsWith(path);
}


const PATH_LABELS = {
  '/': 'لوحة التحكم',
  '/tasks': 'المهام',
  '/recurring-tasks': 'مهام متكررة',
  '/projects': 'المشاريع',
  '/sponsors': 'الداعمون',
  '/report-templates': 'قوالب التقارير',
  '/clients': 'العملاء',
  '/requests': 'طلبات العملاء',
  '/contracts': 'العقود',
  '/workflows': 'المسارات',
  '/book-summaries': 'تلخيص الكتب',
  '/book-summaries/report': 'التقرير الشهري لتلخيص الكتب',
  '/transcription': 'التفريغ',
  '/transcription/search': 'البحث في التفريغات',
  '/reports': 'التقارير',
  '/payroll': 'مستحقات الفريق',
  '/financial-reports': 'التقارير المالية',
  '/financial-settings': 'إعدادات التقارير المالية',
  '/progress-reports': 'تقارير التقدّم',
  '/tags': 'الوسوم',
  '/users': 'المستخدمون',
  '/broadcast': 'رسالة عامة',
  '/activity-logs': 'سجل العمليات',
  '/notifications': 'التنبيهات',
  '/profile': 'ملفي',
  '/help': 'المساعدة',
  '/settings': 'الإعدادات',
};

function pathTitle(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return PATH_LABELS['/'];
  // Walk down to longest match
  let acc = '';
  let label = '';
  for (const s of segments) {
    acc += '/' + s;
    if (PATH_LABELS[acc]) label = PATH_LABELS[acc];
  }
  return label || segments[0];
}

function Breadcrumbs() {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const crumbs = [{ path: '/', label: 'الرئيسية' }];
  let acc = '';
  parts.forEach(p => {
    acc += '/' + p;
    const label = PATH_LABELS[acc] || (isNaN(Number(p)) ? p : `#${p}`);
    crumbs.push({ path: acc, label });
  });
  if (crumbs.length === 1) return null;
  return (
    <nav className="breadcrumbs">
      {crumbs.map((c, i) => (
        <span key={c.path}>
          {i > 0 && <span className="breadcrumb-sep">/</span>}
          {i < crumbs.length - 1
            ? <NavLink to={c.path}>{c.label}</NavLink>
            : <span className="breadcrumb-current">{c.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout, api } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { openTour } = useHelp();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('sidebar-closed-groups') || '[]')); }
    catch { return new Set(); }
  });
  const [notifCount, setNotifCount] = useState(0);
  const [requestAlerts, setRequestAlerts] = useState(0);
  const [approvalAlerts, setApprovalAlerts] = useState(0);
  const canApproveMcp = Array.isArray(user?.permissions) && user.permissions.includes('mcp.connect');
  const navAlerts = { '/requests': requestAlerts, '/approvals': approvalAlerts };

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar-closed-groups', JSON.stringify([...closedGroups]));
  }, [closedGroups]);

  const toggleGroup = useCallback((key) => {
    setClosedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const loadNotifs = useCallback(async () => {
    const d = await api('/notifications/unread-count');
    setNotifCount(d.count);

    // ردود العملاء غير المقروءة والتذاكر المتأخرة — شارة على «طلبات العملاء»
    try {
      const s = await api('/client-requests/stats');
      setRequestAlerts((s.unread || 0) + (s.overdue || 0));
    } catch { setRequestAlerts(0); }

    if (canApproveMcp) {
      try { setApprovalAlerts((await api('/me/mcp-approvals/count')).count); } catch { setApprovalAlerts(0); }
    }
  }, [api, canApproveMcp]);

  useSmartPoll(loadNotifs, { activeMs: 10000, idleMs: 30000, hiddenMs: 120000 });

  // بند المستحقات مالي — الدور وحده لا يكفي، تلزمه الصلاحية الدقيقة
  const visibleGroups = NAV_GROUPS
    .map(g => ({
      ...g,
      items: g.items.filter(n => n.roles.includes(user?.role)
        && (!n.perm || (Array.isArray(user?.permissions) && user.permissions.includes(n.perm)))),
    }))
    .filter(g => g.items.length > 0);
  const visibleNav = visibleGroups.flatMap(g => g.items);
  const currentTitle = pathTitle(location.pathname);

  // Refs for the scrollable bottom-tabs strip and the active tab,
  // used to auto-center the active tab on route change.
  const bottomTabsRef = useRef(null);
  const activeTabRef = useRef(null);

  // Active bottom-tab index for the full nav (used in the scrollable strip)
  const activeBottomIdx = (() => {
    const idx = visibleNav.findIndex(n => isNavActive(location.pathname, n.path));
    return idx === -1 ? 0 : idx;
  })();

  // Auto-scroll the active tab into the center on route change
  useEffect(() => {
    const el = activeTabRef.current;
    const strip = bottomTabsRef.current;
    if (!el || !strip) return;
    const elBox = el.getBoundingClientRect();
    const stripBox = strip.getBoundingClientRect();
    const elCenter = elBox.left + elBox.width / 2;
    const stripCenter = stripBox.left + stripBox.width / 2;
    const delta = elCenter - stripCenter;
    if (Math.abs(delta) > 4) {
      strip.scrollBy({ left: delta, behavior: 'smooth' });
    }
  }, [activeBottomIdx]);

  return (
    <div className={`layout ${collapsed ? 'collapsed' : ''} ${drawerOpen ? 'drawer-open' : ''}`} data-tour="welcome">
      {/* Backdrop for mobile drawer */}
      <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />

      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <Logo kind="mark" variant="white" height={collapsed ? 26 : 32} />
            {!collapsed && <span className="sidebar-brand-name">منصة العمليات</span>}
          </div>
          <button className="btn-icon sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'توسيع' : 'طي'}>
            {collapsed ? '◀' : '▶'}
          </button>
          <button className="btn-icon drawer-close-btn" onClick={() => setDrawerOpen(false)} title="إغلاق" aria-label="إغلاق القائمة">
            ✕
          </button>
        </div>
        <nav>
          {visibleGroups.map(g => {
            // المجموعة التي تضمّ الصفحة الحالية تبقى مفتوحة مهما كان تفضيل الطيّ
            const hasActive = g.items.some(n => isNavActive(location.pathname, n.path));
            const isClosed = closedGroups.has(g.key) && !hasActive;
            return (
              <div key={g.key} className={`nav-group ${isClosed ? 'is-closed' : ''} ${hasActive ? 'has-active' : ''}`}>
                <button
                  type="button" className="nav-group-header"
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={!isClosed}
                >
                  <span className="nav-group-label">{g.label}</span>
                  <span className="nav-group-chevron" aria-hidden="true">⌄</span>
                </button>
                <div className="nav-group-items">
                  {g.items.map(n => (
                    <NavLink
                      key={n.path} to={n.path} end={n.path === '/'}
                      data-tour={n.tour}
                      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                      title={collapsed ? n.label : ''}
                    >
                      <span className="nav-icon"><NavIcon name={n.icon} /></span>
                      <span className="nav-item-label">{n.label}</span>
                      {navAlerts[n.path] > 0 && (
                        <span style={{
                          marginRight: 'auto', minWidth: 20, textAlign: 'center', fontSize: 11, fontWeight: 700,
                          padding: '1px 6px', borderRadius: 999, background: '#fee2e2', color: '#b91c1c',
                        }}>{navAlerts[n.path]}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {!collapsed && <span className="user-name">{user?.name}</span>}
          <button className="btn-icon" onClick={() => { logout(); navigate('/login'); }} title="تسجيل خروج">🚪</button>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          {/* Mobile-only: hamburger + page title */}
          <button
            className="btn-icon mobile-menu-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="فتح القائمة"
          >☰</button>
          <div className="mobile-title">{currentTitle}</div>

          <div className="topbar-right">
            <Breadcrumbs />
          </div>
          <div className="topbar-left">
            <button
              className="btn-icon" data-tour="topbar-help"
              onClick={() => openTour('page')}
              title="جولة تفاعلية لهذه الصفحة"
            >❓</button>
            <button className="btn-icon" onClick={toggleTheme} title={theme === 'dark' ? 'وضع النهار' : 'وضع الليل'}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="btn-icon notif-btn" onClick={() => navigate('/notifications')} title="التنبيهات">
              🔔 {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
            </button>
            <span className="user-badge">{user?.name}</span>
          </div>
        </header>

        <div className="content-area">{children}</div>
      </main>

      {/* Mobile-only: scrollable bottom navigation tabs (all nav items) */}
      <nav className="bottom-tabs" ref={bottomTabsRef}>
        <div className="bottom-tabs-strip">
          {visibleNav.map((n, i) => (
            <NavLink
              key={n.path} to={n.path} end={n.path === '/'}
              ref={i === activeBottomIdx ? activeTabRef : null}
              className={({ isActive }) => `bottom-tab ${isActive ? 'active' : ''}`}
              aria-label={n.label}
            >
              <span className="bottom-tab-icon"><NavIcon name={n.icon} size={22} /></span>
              <span className="bottom-tab-label">{n.label}</span>
            </NavLink>
          ))}
        </div>
        {/* Edge fade hints that more tabs exist on either side */}
        <div className="bottom-tabs-fade bottom-tabs-fade-start" aria-hidden="true" />
        <div className="bottom-tabs-fade bottom-tabs-fade-end" aria-hidden="true" />
      </nav>
    </div>
  );
}
